/**
 * OptionDiscoveryService
 *
 * Handles option discovery, historical price calculation, and performance metrics.
 * Uses point-in-time volatility calculation matching the main app (24h lookback, 5-minute candles).
 */

import {
  formatOptionSymbol,
  createOptionIdentifier,
  TIMEFRAME_MS,
  type OptionIdentifier,
  type OptionListItem,
  type OptionPricePoint,
  type PerformanceTimeframe,
} from '@/types/optionDiscovery';
import { OptionCalculator, getMinOptionPrice } from '@services/optionService';
import { priceService, type SecondPricePoint } from '@services/priceService';
import { RoundService } from '@services/roundService';
import { getCurrentTime } from '@services/timeshiftService';
import type { Asset } from '@shared/types';
import {
  generateDynamicStrikePrices,
  getAssetStrikeMultiple,
} from '@utils/chartHelpers';
import { calculateVolatility } from '@utils/option-math';

const SUPPORTED_ASSETS: Asset[] = ['BTC', 'ETH', 'SOL' /* , 'XRP' */];

/**
 * Volatility lookback period in milliseconds
 * Must match the main volatilityService (24 hours of data)
 */
const VOLATILITY_LOOKBACK_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Candle granularity for volatility calculation (5 minutes = 300 seconds)
 * Must match volatilityService
 */
const VOLATILITY_GRANULARITY_SECONDS = 300;

/**
 * Minimum candles needed for valid volatility (24h of 5-minute candles = 288)
 */
const MIN_CANDLES_FOR_VOLATILITY = 200; // Slightly less than 288 to allow some gaps

/**
 * Cache for round-stable data: strikes, inception prices, and volatility history
 * Resets when round changes
 */
interface RoundCache {
  roundExpiry: number;
  // Cached strike prices per asset (stable for the round)
  strikes: Map<Asset, number[]>;
  // Cached reference spot prices per asset (first seen in round)
  referenceSpots: Map<Asset, number>;
  // Cached inception prices per option
  inceptionPrices: Map<
    string,
    { price: number; spotPrice: number; volatility: number; timestamp: number }
  >;
  // Cached historical volatility per asset per timestamp (immutable once calculated)
  // Key format: "asset-timestamp"
  volatilityHistory: Map<string, number>;
}

let roundCache: RoundCache = {
  roundExpiry: 0,
  strikes: new Map(),
  referenceSpots: new Map(),
  inceptionPrices: new Map(),
  volatilityHistory: new Map(),
};

function ensureRoundCache(): void {
  const currentExpiry = RoundService.getCurrentRoundExpiry();
  if (roundCache.roundExpiry !== currentExpiry) {
    roundCache = {
      roundExpiry: currentExpiry,
      strikes: new Map(),
      referenceSpots: new Map(),
      inceptionPrices: new Map(),
      volatilityHistory: new Map(),
    };
  }
}

function getOptionCacheKey(option: OptionIdentifier): string {
  return `${option.asset}-${option.strikePrice}-${option.type}`;
}

/**
 * Get stable strike prices for an asset (cached for the round)
 */
function getStableStrikesForAsset(asset: Asset): number[] {
  ensureRoundCache();

  const cached = roundCache.strikes.get(asset);
  if (cached) {
    return cached;
  }

  // Get current spot to generate initial strikes
  const priceData = priceService.getPrice(asset);
  if (!priceData?.price) {
    return [];
  }

  // Cache the reference spot and generate strikes
  roundCache.referenceSpots.set(asset, priceData.price);
  const strikes = generateDynamicStrikePrices(priceData.price, asset);
  roundCache.strikes.set(asset, strikes);

  return strikes;
}

/**
 * Get or calculate inception data for an option
 * Inception = first point where we have enough history for valid 24h volatility
 */
function getOrCalculateInception(
  option: OptionIdentifier,
  priceHistory: SecondPricePoint[],
): {
  price: number;
  spotPrice: number;
  volatility: number;
  timestamp: number;
} | null {
  ensureRoundCache();

  const cacheKey = getOptionCacheKey(option);
  const cached = roundCache.inceptionPrices.get(cacheKey);

  if (cached) {
    return cached;
  }

  if (priceHistory.length < MIN_CANDLES_FOR_VOLATILITY) {
    return null;
  }

  // Find the first point where we have enough lookback data for valid volatility
  const earliestTimestamp = priceHistory[0].timestamp;
  const firstValidTimestamp = earliestTimestamp + VOLATILITY_LOOKBACK_MS;

  // Find inception point (first point with valid volatility)
  let inceptionIndex = -1;
  for (const [i, element] of priceHistory.entries()) {
    if (element.timestamp >= firstValidTimestamp) {
      inceptionIndex = i;
      break;
    }
  }

  if (inceptionIndex < 0) {
    return null;
  }

  const inceptionPoint = priceHistory[inceptionIndex];

  // Calculate volatility at inception using 24h of data ending at inception
  const lookbackPrices: number[] = [];
  for (let j = 0; j <= inceptionIndex; j++) {
    const p = priceHistory[j];
    if (p.timestamp >= inceptionPoint.timestamp - VOLATILITY_LOOKBACK_MS) {
      lookbackPrices.push(p.price);
    }
  }

  if (lookbackPrices.length < MIN_CANDLES_FOR_VOLATILITY) {
    return null;
  }

  const inceptionVol = calculateVolatility(
    lookbackPrices,
    VOLATILITY_GRANULARITY_SECONDS,
  );

  if (inceptionVol <= 0) {
    return null;
  }

  const inceptionPrice = calculateOptionPriceAtPoint(
    option,
    inceptionPoint.price,
    inceptionVol,
    inceptionPoint.timestamp,
  );

  // calculateOptionPriceAtPoint now always returns at least minPrice, so this check is redundant
  // But keep it for safety in case minPrice is somehow 0
  if (inceptionPrice <= 0) {
    return null;
  }

  const result = {
    price: inceptionPrice,
    spotPrice: inceptionPoint.price,
    volatility: inceptionVol,
    timestamp: inceptionPoint.timestamp,
  };

  roundCache.inceptionPrices.set(cacheKey, result);
  return result;
}

/**
 * Generate all option identifiers for a given asset using stable strikes
 */
export function generateOptionsForAsset(
  asset: Asset,
  expiryTimestamp: number,
): OptionIdentifier[] {
  // Use cached stable strikes (don't regenerate based on current price)
  const strikePrices = getStableStrikesForAsset(asset);
  const options: OptionIdentifier[] = [];

  for (const strikePrice of strikePrices) {
    // Create both CALL and PUT for each strike
    options.push(
      createOptionIdentifier(asset, strikePrice, 'CALL', expiryTimestamp),
    );
    options.push(
      createOptionIdentifier(asset, strikePrice, 'PUT', expiryTimestamp),
    );
  }

  return options;
}

/**
 * Generate all options across all supported assets (stable for the round)
 */
export function generateAllOptions(): OptionIdentifier[] {
  const expiryTimestamp = RoundService.getCurrentRoundExpiry();
  const allOptions: OptionIdentifier[] = [];

  for (const asset of SUPPORTED_ASSETS) {
    // Just check we have price data for the asset
    const priceData = priceService.getPrice(asset);
    if (!priceData?.price) {
      continue;
    }

    const assetOptions = generateOptionsForAsset(asset, expiryTimestamp);
    allOptions.push(...assetOptions);
  }

  return allOptions;
}

// getMinOptionPrice is imported from optionService for asset-specific minimum prices

/**
 * Calculate option price at a specific point in time
 */
export function calculateOptionPriceAtPoint(
  option: OptionIdentifier,
  spotPrice: number,
  volatility: number,
  timestamp: number,
): number {
  const minPrice = getMinOptionPrice(option.asset);
  
  if (volatility <= 0) {
    // Return minimum price instead of 0 to ensure option is shown
    return minPrice;
  }

  try {
    const tempOption = OptionCalculator.createOption({
      asset: option.asset,
      type: option.type,
      strikePrice: option.strikePrice,
      expiryTimestamp: option.expiryTimestamp,
      purchasePrice: spotPrice, // Not used for pricing
      quantity: 1,
      purchaseTimestamp: timestamp,
    });

    const price = OptionCalculator.getCurrentPrice(
      tempOption,
      spotPrice,
      timestamp,
      volatility,
    );

    // Floor at minimum price to prevent unrealistic percentage gains
    // Always return at least minPrice, even if calculation fails or returns 0
    return price > 0 ? Math.max(price, minPrice) : minPrice;
  } catch {
    // Return minimum price instead of 0 to ensure option is shown even if calculation fails
    return minPrice;
  }
}

/**
 * Get cached volatility or calculate and cache it
 * Historical volatility is immutable - once calculated for timestamp T, it never changes
 */
function getOrCalculateVolatility(
  asset: Asset,
  timestamp: number,
  priceHistory: SecondPricePoint[],
  endIndex: number,
): number | null {
  ensureRoundCache();

  const cacheKey = `${asset}-${timestamp}`;
  const cached = roundCache.volatilityHistory.get(cacheKey);

  if (cached !== undefined) {
    return cached;
  }

  // Collect prices within the 24h lookback window ending at this timestamp
  const lookbackPrices: number[] = [];
  const lookbackStart = timestamp - VOLATILITY_LOOKBACK_MS;

  for (let j = 0; j <= endIndex; j++) {
    const p = priceHistory[j];
    if (p.timestamp >= lookbackStart && p.timestamp <= timestamp) {
      lookbackPrices.push(p.price);
    }
  }

  if (lookbackPrices.length < MIN_CANDLES_FOR_VOLATILITY) {
    return null;
  }

  // Always use 5-minute granularity for volatility calculation
  // The lookback data (before round start) is always 5-minute candles
  // This ensures consistency with inception volatility calculation
  const volatility = calculateVolatility(
    lookbackPrices,
    VOLATILITY_GRANULARITY_SECONDS,
  );

  if (volatility <= 0) {
    return null;
  }

  // Cache it - this value will never change for this timestamp
  roundCache.volatilityHistory.set(cacheKey, volatility);
  return volatility;
}

/**
 * Calculate option price history with ACCURATE point-in-time volatility
 * Uses sliding window approach for O(n) performance instead of O(n²)
 *
 * For each point T in the output:
 * - Uses spot price at T
 * - Calculates volatility using 24h of data ending at T (matches main app)
 * - Uses time-to-expiry at T
 *
 * IMPORTANT: priceHistory must include 24h of data BEFORE the display window
 * to calculate volatility for the earliest displayed point.
 *
 * The output only includes points from when the option was created (round start)
 * to avoid showing history from before the option existed.
 */
export function calculateOptionPriceHistory(
  option: OptionIdentifier,
  priceHistory: SecondPricePoint[],
): OptionPricePoint[] {
  if (priceHistory.length < MIN_CANDLES_FOR_VOLATILITY) {
    return [];
  }

  const optionPriceHistory: OptionPricePoint[] = [];

  // Find the earliest timestamp we can calculate volatility for
  // (need VOLATILITY_LOOKBACK_MS of data before it)
  const earliestTimestamp = priceHistory[0].timestamp;
  const firstValidVolatilityTimestamp =
    earliestTimestamp + VOLATILITY_LOOKBACK_MS;

  // Option only exists from round start (expiry - round duration)
  const roundStartTimestamp = getRoundStartTimestamp();

  // First valid timestamp is the later of: volatility requirement OR option creation
  const firstValidTimestamp = Math.max(
    firstValidVolatilityTimestamp,
    roundStartTimestamp,
  );

  // Use sliding window: track start index of lookback window
  let windowStartIndex = 0;

  // For each point in price history
  for (let i = 0; i < priceHistory.length; i++) {
    const point = priceHistory[i];

    // Only process points from round start onwards (option didn't exist before)
    if (point.timestamp < roundStartTimestamp) {
      continue;
    }

    // Advance window start to maintain 24h lookback (if we have enough data)
    const lookbackStart = point.timestamp - VOLATILITY_LOOKBACK_MS;
    while (
      windowStartIndex < i &&
      priceHistory[windowStartIndex].timestamp < lookbackStart
    ) {
      windowStartIndex++;
    }

    // Try to get or calculate volatility (may be null if not enough history)
    let volatility: number | null = null;
    if (point.timestamp >= firstValidVolatilityTimestamp) {
      // Only try to calculate volatility if we have enough lookback data
      volatility = getOrCalculateVolatility(
        option.asset,
        point.timestamp,
        priceHistory,
        i,
      );
    }

    // If volatility is null (not enough history), use minimum price to ensure we still show the point
    // This is important for deep OTM options and early points in the option's lifetime
    const minPrice = getMinOptionPrice(option.asset);
    let optionPrice: number;
    
    if (volatility === null || volatility <= 0) {
      // Use minimum price when volatility can't be calculated or is invalid
      optionPrice = minPrice;
    } else {
      // Calculate option price at this point with point-in-time data
      optionPrice = calculateOptionPriceAtPoint(
        option,
        point.price, // spot at time T
        volatility, // 24h volatility calculated from data up to T
        point.timestamp, // time T (affects time-to-expiry)
      );

      // Ensure price is at least minimum (safety check in case calculation fails)
      if (!isFinite(optionPrice) || optionPrice <= 0) {
        optionPrice = minPrice;
      } else {
        optionPrice = Math.max(optionPrice, minPrice);
      }
    }

    // Always add the point to show a complete history
    // This ensures deep OTM options show a flat line at minimum price
    optionPriceHistory.push({
      timestamp: point.timestamp,
      price: optionPrice,
      spotPrice: point.price,
      volatility: volatility ?? 0, // Use 0 as placeholder when volatility is null
    });
  }

  return optionPriceHistory;
}

/**
 * Fetch extended price history for option chart calculations
 *
 * Uses a dual-fetch approach for optimal granularity:
 * - 5-minute candles for 24h volatility lookback (288 candles)
 * - 1-minute candles for option lifetime display (finer resolution)
 *
 * The two datasets are merged, with 1-minute data taking precedence
 * where available for smoother charts.
 *
 * @param asset - The asset to fetch history for
 * @param optionLifetimeMs - How long the option has been alive (from round start to now)
 */
export async function fetchExtendedPriceHistory(
  asset: Asset,
  optionLifetimeMs?: number,
): Promise<SecondPricePoint[]> {
  const now = getCurrentTime();

  // Calculate actual option lifetime from round start
  const roundStart = getRoundStartTimestamp();
  const actualLifetimeMs = now - roundStart;

  // Use provided lifetime or actual lifetime
  const lifetimeMs = optionLifetimeMs ?? actualLifetimeMs;

  try {
    // Fetch 5-minute candles for volatility calculation
    // We need 24h of data BEFORE round start to calculate volatility at option creation
    // So start time = roundStart - 24h - 1h buffer
    const volatilityStartTime = new Date(
      roundStart - VOLATILITY_LOOKBACK_MS - 60 * 60 * 1000,
    );

    // Calculate how many 5-minute candles we need from volatility start to now
    const fiveMinCandlesNeeded = Math.ceil(
      (now - volatilityStartTime.getTime()) / (5 * 60 * 1000),
    );

    const fiveMinCandles = await priceService.fetchCandles(
      asset,
      '5m',
      Math.min(fiveMinCandlesNeeded, 1440), // API limit is 1440
      volatilityStartTime.toISOString(),
      new Date(now).toISOString(),
    );

    // Fetch 1-minute candles for option lifetime display (finer resolution)
    // Only fetch if option lifetime is meaningful (> 0)
    let oneMinCandles: Array<{ close: string; openTime: string }> = [];
    if (lifetimeMs > 0) {
      const displayStartTime = new Date(roundStart - 5 * 60 * 1000); // Start slightly before round
      const oneMinCandlesNeeded = Math.min(
        Math.ceil(lifetimeMs / (60 * 1000)) + 10,
        1440,
      );

      oneMinCandles = await priceService.fetchCandles(
        asset,
        '1m',
        oneMinCandlesNeeded,
        displayStartTime.toISOString(),
        new Date(now).toISOString(),
      );
    }

    // Convert 5-minute candles to SecondPricePoint format
    const fiveMinPoints: SecondPricePoint[] = fiveMinCandles.map(candle => ({
      price: parseFloat(candle.close),
      timestamp: new Date(candle.openTime).getTime(),
    }));

    // Convert 1-minute candles to SecondPricePoint format
    const oneMinPoints: SecondPricePoint[] = oneMinCandles.map(candle => ({
      price: parseFloat(candle.close),
      timestamp: new Date(candle.openTime).getTime(),
    }));

    // Merge the two datasets:
    // - Use 5-minute data for volatility lookback (before round start)
    // - Use 5-minute data for early option lifetime if 1-minute data doesn't cover it
    // - Use 1-minute data for recent option lifetime (finer resolution)
    const mergedPoints: SecondPricePoint[] = [];

    // Find the earliest 1-minute timestamp (if any)
    const earliestOneMinTimestamp =
      oneMinPoints.length > 0
        ? Math.min(...oneMinPoints.map(p => p.timestamp))
        : now;

    // Add 5-minute points for:
    // 1. Volatility lookback (before round start)
    // 2. Early option lifetime not covered by 1-minute data
    for (const point of fiveMinPoints) {
      if (point.timestamp < earliestOneMinTimestamp) {
        mergedPoints.push(point);
      }
    }

    // Add 1-minute points for recent history (finer resolution)
    for (const point of oneMinPoints) {
      mergedPoints.push(point);
    }

    // Sort by timestamp and remove duplicates (prefer later entries for same timestamp)
    const uniquePoints = new Map<number, SecondPricePoint>();
    for (const point of mergedPoints) {
      uniquePoints.set(point.timestamp, point);
    }

    return Array.from(uniquePoints.values()).sort(
      (a, b) => a.timestamp - b.timestamp,
    );
  } catch (error) {
    console.error('Failed to fetch extended price history:', error);
    return [];
  }
}

/**
 * Get the round start timestamp (inception for performance calculation)
 * Always returns 16 hours ago rounded to the nearest hour
 */
export function getRoundStartTimestamp(): number {
  return RoundService.getRoundStartTimestamp();
}

/**
 * Calculate performance metrics for a single option
 *
 * Inception price: Uses historic spot, historic vol, historic time (more time to expiry)
 * Current price: Uses current spot, current vol, current time (less time to expiry)
 */
/**
 * Calculate performance with optional live spot price override
 * If liveSpotPrice is provided, uses it for current price calculation (real-time updates)
 * Otherwise falls back to historical data
 */
export function calculateOptionPerformance(
  option: OptionIdentifier,
  priceHistory: SecondPricePoint[],
  liveSpotPrice?: number,
): OptionListItem | null {
  if (priceHistory.length < MIN_CANDLES_FOR_VOLATILITY) {
    return null;
  }

  // Get or calculate cached inception data (stable within round)
  const inception = getOrCalculateInception(option, priceHistory);
  if (!inception) {
    return null;
  }

  // Current price uses: current spot, current vol, current time
  const latestPoint = priceHistory[priceHistory.length - 1];
  // Use live spot price if provided, otherwise use historical data
  const currentSpotPrice = liveSpotPrice ?? latestPoint.price;

  // Calculate current volatility using 24h of data ending at latest point
  // This uses the same method as volatilityService (5-minute candles, EWMA)
  const currentVolatility = getOrCalculateVolatility(
    option.asset,
    latestPoint.timestamp,
    priceHistory,
    priceHistory.length - 1,
  );

  if (currentVolatility === null) {
    return null;
  }

  // Current price with current time (less time to expiry)
  // Note: calculateOptionPriceAtPoint always returns at least minPrice (never 0)
  const currentPrice = calculateOptionPriceAtPoint(
    option,
    currentSpotPrice,
    currentVolatility,
    getCurrentTime(),
  );

  // Both prices should always be >= minPrice now, but keep validation for safety
  const minPrice = getMinOptionPrice(option.asset);
  const validCurrentPrice = currentPrice > 0 ? currentPrice : minPrice;
  const validInceptionPrice = inception.price > 0 ? inception.price : minPrice;

  // This check should never fail now, but keep it for safety
  if (validCurrentPrice <= 0 || validInceptionPrice <= 0) {
    return null;
  }

  // Use the valid prices (actual calculated prices, only floored if they were 0)
  // This preserves accurate performance percentages for legitimately low-priced options
  const priceChange = validCurrentPrice - validInceptionPrice;
  const priceChangePercent = (priceChange / validInceptionPrice) * 100;

  return {
    ...option,
    symbol: formatOptionSymbol(option.asset, option.strikePrice, option.type),
    currentPrice: validCurrentPrice,
    inceptionPrice: validInceptionPrice,
    priceChange,
    priceChangePercent,
    spotPrice: currentSpotPrice,
  };
}

/**
 * Check if there's enough history for a given timeframe
 */
export function hasEnoughHistory(
  priceHistory: SecondPricePoint[],
  timeframe: PerformanceTimeframe,
): boolean {
  if (timeframe === 'all' || priceHistory.length === 0) {
    return true;
  }

  const timeframeMs = TIMEFRAME_MS[timeframe];
  const now = getCurrentTime();
  const oldestPoint = priceHistory[0];

  // Need history going back at least the timeframe duration
  return now - oldestPoint.timestamp >= timeframeMs;
}

/**
 * Get available timeframes based on price history
 */
export function getAvailableTimeframes(
  priceHistory: SecondPricePoint[],
): PerformanceTimeframe[] {
  const timeframes: PerformanceTimeframe[] = ['all'];

  if (priceHistory.length === 0) {
    return timeframes;
  }

  const now = getCurrentTime();
  const oldestTimestamp = priceHistory[0].timestamp;
  const historyDuration = now - oldestTimestamp;

  // Add timeframes that we have enough history for
  const orderedTimeframes: PerformanceTimeframe[] = [
    '15m',
    '30m',
    '1h',
    '2h',
    '4h',
    '8h',
    '12h',
  ];

  for (const tf of orderedTimeframes) {
    if (historyDuration >= TIMEFRAME_MS[tf]) {
      timeframes.push(tf);
    }
  }

  return timeframes;
}

/**
 * Get strike price step for an asset
 */
export function getStrikeStep(asset: Asset): number {
  return getAssetStrikeMultiple(asset);
}

export const optionDiscoveryService = {
  generateOptionsForAsset,
  generateAllOptions,
  calculateOptionPriceAtPoint,
  calculateOptionPriceHistory,
  calculateOptionPerformance,
  fetchExtendedPriceHistory,
  getRoundStartTimestamp,
  hasEnoughHistory,
  getAvailableTimeframes,
  getStrikeStep,
};
